// ===== UNIVERSAL ANALYSIS SERVICE =====

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api';

// ===== INTERFACES =====

export interface Citation {
  id: string;
  sourceDocId: string;
  section: string;
  page: string | number;
  text: string;
  clickable: boolean;
  url: string;
  startChar?: number;
  endChar?: number;
}

export interface UniversalJsonResponse {
  responseMetadata: {
    workflowType: 'policy_comparison' | 'policy_analysis' | 'loss_run_analysis' | 'research_analysis' | 'risk_assessment';
    generatedAt: string;
    documentsAnalyzed: Array<{
      id: string;
      name: string;
      type: string;
      policyType?: string;
    }>;
    processingTime?: number;
    confidence: 'High' | 'Medium' | 'Low';
  };
  
  executiveSummary: {
    paragraphs: Array<{
      content: string;
      citations: Citation[];
    }>;
  };
  
  analysisMatrices: Array<{
    title: string;
    type: string;
    description?: string;
    headers: string[];
    rows: Array<{
      [key: string]: any;
      citations?: Citation[];
    }>;
  }>;
  
  keyFindings: {
    summary: string;
    findings: Array<{
      category: string;
      finding: string;
      impact: 'Positive' | 'Negative' | 'Neutral' | 'Critical';
      confidence: 'High' | 'Medium' | 'Low';
      priority: 'High' | 'Medium' | 'Low';
      citations: Citation[];
    }>;
  };
  
  recommendations?: Array<{
    priority: 'Critical' | 'High' | 'Medium' | 'Low';
    category: string;
    action: string;
    rationale: string;
    timeline?: string;
    supportingCitations: Citation[];
  }>;
  
  citationIndex: {
    totalCitations: number;
    documentSources: Array<{
      docId: string;
      name: string;
      citationCount: number;
      baseUrl: string;
    }>;
  };
}

export interface WorkflowExecutionRequest {
  workflowType: 'policy_comparison' | 'policy_analysis' | 'loss_run_analysis' | 'research_analysis' | 'risk_assessment';
  documents: Array<{
    id: string;
    name?: string;
  }>;
  additionalContext?: string;
  customerId?: string;
  projectId?: string;
  options?: {
    smartCitations?: boolean;
    enhanceCitations?: boolean;
    validateCitations?: boolean;
  };
}

export interface WorkflowExecutionResponse {
  success: boolean;
  workflowId: string;
  workflowType: string;
  results: UniversalJsonResponse;
  metadata: {
    documentsProcessed: number;
    citationsGenerated: number;
    executionTime?: number;
    confidence: string;
    generatedAt: string;
  };
}

export interface EnhancedComparisonRequest {
  documentIds: string[];
  additionalFacts?: string;
  options?: {
    smartCitations?: boolean;
    enhanceCitations?: boolean;
    validateCitations?: boolean;
  };
}

export interface EnhancedComparisonResponse {
  success: boolean;
  report: UniversalJsonResponse;
  metadata: {
    documentIds: string[];
    documentsCompared: Array<{
      id: string;
      name: string;
      policyType?: string;
      uploadedAt: string;
    }>;
    citationsGenerated: number;
    processingTime: number;
    confidence: string;
    generatedAt: string;
    responseType: 'universal_json';
  };
  citations: Citation[];
  citationIndex: any;
}

export interface WorkflowListResponse {
  success: boolean;
  workflows: Array<{
    id: string;
    workflowType: string;
    status: string;
    currentStep: string;
    createdAt: string;
    updatedAt: string;
    documentsCount: number;
  }>;
  pagination: {
    total: number;
    limit: number;
    offset: number;
    hasMore: boolean;
  };
}

// ===== SERVICE CLASS =====

class UniversalAnalysisService {
  
  // Get authentication token from localStorage
  private getAuthToken(): string | null {
    return localStorage.getItem('riskninja-token');
  }
  
  // Create headers with authentication
  private getHeaders(): HeadersInit {
    const token = this.getAuthToken();
    return {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` })
    };
  }
  
  // Execute universal workflow
  async executeWorkflow(request: WorkflowExecutionRequest): Promise<WorkflowExecutionResponse> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/workflows/execute`, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify(request)
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to execute workflow');
      }
      
      return await response.json();
    } catch (error) {
      console.error('Workflow execution failed:', error);
      throw error;
    }
  }
  
  // Execute enhanced comparison
  async executeEnhancedComparison(request: EnhancedComparisonRequest): Promise<EnhancedComparisonResponse> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/comparison/generate-enhanced`, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify(request)
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to generate enhanced comparison');
      }
      
      return await response.json();
    } catch (error) {
      console.error('Enhanced comparison failed:', error);
      throw error;
    }
  }
  
  // Execute workflow-based comparison
  async executeWorkflowComparison(
    documentIds: string[], 
    workflowType: string, 
    additionalFacts?: string
  ): Promise<any> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/comparison/generate-workflow`, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify({
          documentIds,
          workflowType,
          additionalFacts
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to generate workflow comparison');
      }
      
      return await response.json();
    } catch (error) {
      console.error('Workflow comparison failed:', error);
      throw error;
    }
  }
  
  // Get workflow execution results
  async getWorkflowResults(workflowId: string): Promise<any> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/workflows/execute/${workflowId}`, {
        method: 'GET',
        headers: this.getHeaders()
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to get workflow results');
      }
      
      return await response.json();
    } catch (error) {
      console.error('Failed to get workflow results:', error);
      throw error;
    }
  }
  
  // List user's workflow executions
  async listWorkflowExecutions(
    workflowType?: string, 
    limit: number = 20, 
    offset: number = 0
  ): Promise<WorkflowListResponse> {
    try {
      const params = new URLSearchParams({
        limit: limit.toString(),
        offset: offset.toString()
      });
      
      if (workflowType) {
        params.append('workflowType', workflowType);
      }
      
      const response = await fetch(`${API_BASE_URL}/api/workflows/executions?${params}`, {
        method: 'GET',
        headers: this.getHeaders()
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to list workflows');
      }
      
      return await response.json();
    } catch (error) {
      console.error('Failed to list workflows:', error);
      throw error;
    }
  }
  
  // Handle citation clicks - navigate to document viewer
  handleCitationClick(citation: Citation, navigate: (path: string, options?: any) => void): void {
    if (!citation.clickable || !citation.url) {
      console.warn('Citation is not clickable or missing URL');
      return;
    }
    
    // Parse the citation URL to extract navigation parameters
    const urlParts = citation.url.split('#');
    const docPath = urlParts[0]; // e.g., "/documents/doc_id"
    const fragment = urlParts[1]; // e.g., "page=3&section=coverage-limits"
    
    if (fragment) {
      const params = new URLSearchParams(fragment);
      const page = params.get('page');
      const section = params.get('section');
      
      navigate(docPath, {
        state: {
          page: page ? parseInt(page) : 1,
          section: section || '',
          highlight: true,
          citationText: citation.text
        }
      });
    } else {
      navigate(docPath);
    }
  }
  
  // Validate workflow type
  isValidWorkflowType(workflowType: string): boolean {
    const validTypes = [
      'policy_comparison',
      'policy_analysis', 
      'loss_run_analysis',
      'research_analysis',
      'risk_assessment'
    ];
    return validTypes.includes(workflowType);
  }
  
  // Get minimum document requirements for workflow type
  getMinDocumentRequirements(workflowType: string): number {
    const requirements: { [key: string]: number } = {
      'policy_comparison': 2,
      'policy_analysis': 1,
      'loss_run_analysis': 1,
      'research_analysis': 1,
      'risk_assessment': 1
    };
    
    return requirements[workflowType] || 1;
  }
  
  // Validate document requirements for workflow
  validateDocumentRequirements(workflowType: string, documentCount: number): { valid: boolean; message?: string } {
    const minRequired = this.getMinDocumentRequirements(workflowType);
    
    if (documentCount < minRequired) {
      return {
        valid: false,
        message: `${workflowType.replace('_', ' ')} requires at least ${minRequired} document${minRequired > 1 ? 's' : ''}`
      };
    }
    
    return { valid: true };
  }
  
  // Export analysis results to different formats
  async exportAnalysis(
    workflowId: string, 
    format: 'pdf' | 'word' | 'excel' | 'json' = 'pdf'
  ): Promise<Blob> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/workflows/export/${workflowId}`, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify({ format })
      });
      
      if (!response.ok) {
        throw new Error('Failed to export analysis');
      }
      
      return await response.blob();
    } catch (error) {
      console.error('Export failed:', error);
      throw error;
    }
  }
  
  // Get workflow statistics
  async getWorkflowStats(userId?: string): Promise<any> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/workflows/stats`, {
        method: 'GET',
        headers: this.getHeaders()
      });
      
      if (!response.ok) {
        throw new Error('Failed to get workflow statistics');
      }
      
      return await response.json();
    } catch (error) {
      console.error('Failed to get workflow stats:', error);
      throw error;
    }
  }
}

// Export singleton instance
export const universalAnalysisService = new UniversalAnalysisService();
export default universalAnalysisService;